import { useEffect, useState } from 'react';
import { api } from '../services/api';
import type { ProfileStatus, User } from '../types';

interface ProfileManagerProps {
  user: User;
  onClose: () => void;
  onProfileDeleted: () => void;
  onLogout?: () => void;
}

const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

const formatDate = (value?: string): string => {
  if (!value) return 'No profile data yet';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const profileErrorText = (err: unknown): string => {
  const payload = (err as { response?: { data?: { error?: string } } })?.response?.data;
  return payload?.error || (err instanceof Error ? err.message : 'Profile request failed');
};

export function ProfileManager({ user, onClose, onProfileDeleted, onLogout }: ProfileManagerProps) {
  const [status, setStatus] = useState<ProfileStatus | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const refresh = async () => {
    setBusy(true);
    setError(null);
    try {
      setStatus(await api.getProfileStatus());
    } catch (err: unknown) {
      setError(profileErrorText(err));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const deleteProfile = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.deleteProfile();
      setMessage('Persistent profile archived. A fresh profile will be created next time the VDI starts.');
      setConfirmDelete(false);
      setStatus({ exists: false, size_bytes: 0, active_session: false });
      onProfileDeleted();
    } catch (err: unknown) {
      setError(profileErrorText(err));
    } finally {
      setBusy(false);
    }
  };

  const resetSecondFactors = async () => {
    if (!confirm('Reset your enrolled VDI second factors? You will set them up again on next sign-in.')) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.resetOwnMFA();
      setMessage('Second factors reset. Sign in again to enroll a new factor.');
      window.setTimeout(() => {
        if (onLogout) onLogout();
      }, 1200);
    } catch (err: unknown) {
      setError(profileErrorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="profile-manager-shell">
      <div className="profile-manager-panel">
        <div className="profile-manager-header">
          <div>
            <h2>VDI Profile</h2>
            <p>{user.realname || user.username}</p>
          </div>
          <button type="button" className="profile-manager-close" onClick={onClose} disabled={busy}>
            Close
          </button>
        </div>

        {message && <div className="profile-manager-status success">{message}</div>}
        {error && <div className="profile-manager-status error">{error}</div>}

        <div className="profile-manager-grid">
          <section className="profile-manager-card">
            <div className="profile-card-title">Persistent Profile</div>
            <div className="profile-stat-row">
              <span>Status</span>
              <strong>{status?.exists ? 'Present' : 'Fresh profile pending'}</strong>
            </div>
            <div className="profile-stat-row">
              <span>Size</span>
              <strong>{status ? formatSize(status.size_bytes) : 'Checking...'}</strong>
            </div>
            <div className="profile-stat-row">
              <span>Last Changed</span>
              <strong>{formatDate(status?.last_modified)}</strong>
            </div>
            <div className="profile-card-actions">
              <button type="button" onClick={refresh} disabled={busy}>
                Refresh
              </button>
              <button
                type="button"
                className={`danger ${confirmDelete ? 'confirm' : ''}`}
                onClick={deleteProfile}
                disabled={busy}
              >
                {confirmDelete ? 'Confirm Delete Profile' : 'Delete Profile'}
              </button>
            </div>
            <p className="profile-card-note">
              Delete archives the profile and stops any active VDI session. The next launch starts clean.
            </p>
          </section>

          <section className="profile-manager-card">
            <div className="profile-card-title">Second Factors</div>
            <p className="profile-card-note">
              Resetting your second factors removes the enrolled authenticator methods for VDI access.
            </p>
            <div className="profile-card-actions">
              <button type="button" onClick={resetSecondFactors} disabled={busy}>
                Reset Second Factors
              </button>
            </div>
            <div className="profile-factor-roadmap">
              <span>PIVKey / CAC</span>
              <strong>Planned</strong>
              <p>PIVKey plus PIN and CAC support will be added as an additional VDI second factor path.</p>
            </div>
          </section>

          <section className="profile-manager-card wide">
            <div className="profile-card-title">Files</div>
            <p className="profile-card-note">
              Use the Files button on the left bar to upload into your persistent Desktop. Uploads work even when the VDI is not running.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
